import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  Grid,
  TextField,
  Button,
  IconButton,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  InputLabel,
} from '@material-ui/core';
import { Formik } from 'formik';
import CloseIcon from '@material-ui/icons/Close';
import clsx from 'clsx';

// UTILITY
import { trimCharacters } from '@/utility/common';
import { setYupValidationSchema } from '@/utility/validationSchema/validationSchema';
import store from '@/utility/store';

// STORE
import { CountiesActions } from '@/store/actions';

// HOOKS
import useActions from '@/hooks/useActions';

// COMPONENTS
import RadioCard from '@/components/Form/RadioCard';

// STYLES
import { FormStyled, DialogStyled } from './styles';

const initialFormData = {
  zipcode: '',
  dma_id: '',
  county_id_fk: '',
};

const ZipCode = ({ className, label, labelComponent, onSubmit, analyticsLocation, ...props }) => {
  const [t] = useTranslation();

  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(true);
  const [formData, setFormData] = useState(initialFormData);
  const ref = useRef(null);
  const validationSchema = useMemo(() => setYupValidationSchema('zipSchema', t));

  const { unique_id, partner, counties, countiesLoading } = useSelector(
    ({ auth, partner: partnerStore, counties: countiesStore }) => ({
      unique_id: auth.unique_id,
      partner: partnerStore.data,
      counties: countiesStore.counties,
      countiesLoading: countiesStore.loading,
    }),
  );

  const [cleanCountiesState, getCounties] = useActions([
    CountiesActions.cleanCountiesState.action,
    requestData => CountiesActions.getCounties.action(requestData),
  ]);

  const handleEditMode = useCallback(() => {
    if (!editMode) {
      store.remove('zipcode');
      store.remove('dma_id');
      store.remove('county_id_fk');

      setFormData(initialFormData);
      onSubmit(initialFormData);
    }

    setEditMode(state => !state);
  }, [editMode]);

  const setCountyData = useCallback(
    county => {
      const dma_id = county.dma_id_fk.toString();
      const county_id_fk = county.county_id.toString();
      const values = ref?.current?.values || {};

      ref.current.setFieldValue('dma_id', dma_id);
      ref.current.setFieldValue('county_id_fk', county_id_fk);

      store.set('zipcode', values.zipcode);
      store.set('dma_id', dma_id);
      store.set('county_id_fk', county_id_fk);

      setOpen(false);
      handleEditMode();

      const newFormData = {
        ...values,
        dma_id,
        county_id_fk,
      };

      setTimeout(() => {
        cleanCountiesState();
        setFormData(newFormData);
        onSubmit(newFormData);
      }, 0);
    },
    [ref.current],
  );

  const handleClose = useCallback(() => {
    if (ref.current) {
      if (ref.current.values.dma_id === '' && ref.current.values.county_id_fk === '') {
        setCountyData(counties[0]);
      }
    }

    setOpen(false);
  });

  useEffect(() => {
    const zipcode = store.get('zipcode') || '';
    const dma_id = store.get('dma_id') || '';
    const county_id_fk = store.get('county_id_fk') || '';

    setFormData({ dma_id, county_id_fk, zipcode });
    setEditMode(!zipcode);
  }, []);

  useEffect(() => {
    if (counties && counties.length) {
      if (counties.length === 1) {
        setCountyData(counties[0]);
      } else {
        setOpen(true);
      }
    }
  }, [counties]);

  const handleZipcode = useCallback(
    values => {
      if (values.zipcode) {
        const requestData = {
          zipcode: values.zipcode,
          partner_id_fk: partner?.id,
          unique_id,
          location: analyticsLocation,
        };

        getCounties(requestData);
      }
    },
    [partner, unique_id],
  );

  const handleZip = useCallback(event => {
    ref.current.setFieldValue('zipcode', trimCharacters(event.target.value));
  });

  return (
    <Formik
      innerRef={ref}
      initialValues={formData}
      validationSchema={validationSchema}
      onSubmit={handleZipcode}
      enableReinitialize
    >
      {({ values, errors, touched, handleBlur, handleSubmit, isValid, isSubmitting }) => {
        return (
          <FormStyled autoComplete="off" {...props} className={clsx('zipcode', className)}>
            {labelComponent || (
              <InputLabel className="zipcode__input-label m-b-10">
                {label ||
                  t(
                    `inputs.${
                      editMode ? 'enterZipGetFullChannelList' : 'showingChannelsAvailableIn'
                    }`,
                  )}
              </InputLabel>
            )}
            <TextField
              placeholder={t(`inputs.${partner?.slug === 'wow' ? 'zipcodeEnter' : 'zipcode'}`)}
              InputProps={{
                endAdornment: (
                  <Button
                    className="zipcode__button m-l-5"
                    type="button"
                    color={editMode ? 'primary' : 'secondary'}
                    size="small"
                    disabled={(!isValid && !isSubmitting) || countiesLoading}
                    onClick={editMode ? handleSubmit : handleEditMode}
                  >
                    {editMode ? t('buttons.submit') : t('buttons.edit')}
                  </Button>
                ),
              }}
              type="tel"
              name="zipcode"
              value={values.zipcode}
              onChange={handleZip}
              onBlur={handleBlur}
              error={touched.zipcode && !!errors.zipcode}
              helperText={touched.zipcode && errors.zipcode}
              className="zipcodeinput zipcode__input"
              disabled={countiesLoading || !editMode}
            />
            {counties && (
              <DialogStyled
                className="dialog"
                open={open}
                onClose={handleClose}
                fullWidth
                maxWidth="md"
              >
                <IconButton
                  className="dialog__close-button"
                  aria-label="close"
                  color="inherit"
                  onClick={handleClose}
                >
                  <CloseIcon fontSize="inherit" color="inherit" />
                </IconButton>

                <DialogTitle className="dialog__title" disableTypography>
                  <Typography variant="h4" align="center">
                    {t(`start.counties.title`)}
                  </Typography>
                </DialogTitle>

                <DialogContent className="dialog__content">
                  <Grid container spacing={1} justifyContent="center">
                    {counties.map(county => (
                      <Grid item xs={6} sm={6} md={3} key={county.county_id}>
                        <RadioCard
                          name="county_id_fk"
                          text={county.county_name}
                          handleChange={() => setCountyData(county)}
                          handleBlur={handleBlur}
                          value={county.county_id.toString()}
                          checkedValue={values.county_id_fk}
                          dataAttributes={{
                            'data-dma_id_fk': county.dma_id_fk.toString(),
                            zipcode: values.zipcode,
                          }}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </DialogContent>
                <DialogActions className="dialog__actions">
                  <Button
                    className="dialog__button"
                    type="submit"
                    color="primary"
                    onClick={handleClose}
                  >
                    {t('buttons.skip')}
                  </Button>
                </DialogActions>
              </DialogStyled>
            )}
          </FormStyled>
        );
      }}
    </Formik>
  );
};

ZipCode.defaultProps = {
  className: '',
  label: '',
  labelComponent: null,
};

ZipCode.propTypes = {
  className: PropTypes.string,
  label: PropTypes.string,
  labelComponent: PropTypes.node,
  onSubmit: PropTypes.func.isRequired,
  analyticsLocation: PropTypes.string.isRequired,
};

export default React.memo(ZipCode);
